import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Eye, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";

export const PortalDialog = ({ clientId }) => {
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState([]);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get(`/clients/${clientId}/portal-links`).then((r) => setLinks(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/portal-links`);
      setLinks((ls) => [...ls, data]);
      toast.success("Portal link created");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = (t) => {
    navigator.clipboard.writeText(`${window.location.origin}/portal/${t}`);
    toast.success("Link copied");
  };

  const revoke = async (t) => {
    try {
      await api.post(`/clients/${clientId}/portal-links/${t}/revoke`);
      setLinks((ls) => ls.filter((l) => l.token !== t));
      toast.success("Link revoked");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="portal-dialog-button">
          <Eye size={16} className="mr-1.5" /> View as client
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="portal-dialog">
        <DialogHeader>
          <DialogTitle>Client portal</DialogTitle>
          <DialogDescription>
            A no-login mini-portal for the client & family: their signed documents, the client handbook, care plan status, and office contacts. Links stay active for 90 days and can be revoked anytime.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {links.length === 0 && <p className="text-sm text-stone-400" data-testid="portal-no-links">No active portal links yet.</p>}
          {links.map((l) => (
            <div key={l.token} className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2" data-testid={`portal-link-${l.token.slice(0, 8)}`}>
              <span className="min-w-0 flex-1 truncate text-xs text-stone-600">{window.location.origin}/portal/{l.token}</span>
              <span className="shrink-0 text-[10px] text-stone-400">expires {String(l.expires_at).slice(0, 10)}</span>
              <button onClick={() => copy(l.token)} className="shrink-0 text-stone-500 hover:text-stone-900" data-testid={`portal-copy-${l.token.slice(0, 8)}`}><Copy size={15} /></button>
              <button onClick={() => revoke(l.token)} className="shrink-0 text-stone-400 hover:text-[#C0392B]" data-testid={`portal-revoke-${l.token.slice(0, 8)}`}><X size={15} /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button onClick={create} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="create-portal-link-button">
            {busy ? "Creating..." : "Create Portal Link"}
          </Button>
          <Button variant="outline" onClick={() => { setOpen(false); navigate(`/clients/${clientId}/portal`); }} className="border-stone-300" data-testid="portal-preview-button">
            Preview as client
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
